import React, { useEffect, useRef, useState } from 'react'
import anime from 'animejs'
import delay from 'delay'
import { Link, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import ScienceIcon from '@mui/icons-material/Science'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import PlumbingIcon from '@mui/icons-material/Plumbing'
import FolderIcon from '@mui/icons-material/Folder'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import LayoutMain from '../layouts/Main'
import LayoutBase from '../layouts/Base'
import imageHsAirplanes from '../assets/hs-airplanes-001.png'
import {
  MESSAGE_PIX_TEMPLATE,
  MESSAGE_SIMPLE_TEMPLATE,
  MESSAGE_FILE_TEMPLATE,
  MESSAGE_FILE_AND_TEXT_TEMPLATE,
  MESSAGE_SPLIT_TEMPLATE } from '../libs/apiDocumentation'
import Text from '../components/Text'
import Logo from '../components/Logo'
import PhoneDevice from '../components/PhoneDevice'
import TestSendMessageModal from '../components/TestSendMessageModal'
import Code from '../components/Code'
import LinkWithQuery from '../components/LinkWithQuery'

export default function DocumentationPage() {
  const theme = useTheme()
  const asideMenuRef = useRef()
  const airplanesRef = useRef()

  const [userApi, setUserApi] = useState('')
  const [openTestSendMessageModal, setOpenTestSendMessageModal] = useState(false)
  const [apiTestData, setApiTestData] = useState({
    title: '',
    template: '',
    httpMethod: ''
  })

  const handleAsideMenuAnimation = async () => {
    await delay(300)
    if(!asideMenuRef.current) return
    anime({
      targets: asideMenuRef.current,
      translateX: [-50, 0],
      opacity: [0, 1]
    })
  }

  const handleAirplanesAnimation = async () => {
    await delay(500)
    if(!airplanesRef.current) return
    anime({
      targets: airplanesRef.current.children,
      translateX: [-50, 0],
      translateY: [50, 0],
      delay: anime.stagger(100),
      opacity: [0, 1]
    })
  }

  const handleOpenTestSendMessageModal = () => {
    setOpenTestSendMessageModal(true)
  }

  const handleCloseTestSendMessageModal = () => {
    setOpenTestSendMessageModal(false)
  }

  const getUserApiFromStorage = () => {
    setUserApi(window.localStorage.getItem('api'))
  }

  useEffect(() => {
    handleAsideMenuAnimation()
    handleAirplanesAnimation()
    getUserApiFromStorage()
  }, [])

  return (
    <LayoutBase>
      <TestSendMessageModal
        open={openTestSendMessageModal}
        title={apiTestData?.title}
        httpMethod={apiTestData?.httpMethod}
        template={apiTestData?.template}
        onClose={() => setOpenTestSendMessageModal(false)}
      />
      <LayoutMain showMainMenu={false}>
        <div className="flex h-full">
          <div className="hidden md:block w-[300px] h-full">
            <div ref={asideMenuRef} className="opacity-0 fixed w-[300px] h-full">
              <Paper className="h-full w-full" elevation={0}>
                <div className="w-full h-full shadow-[5px_0px_10px_0px_rgba(0,0,0,0.3)]">
                  <div className="px-4 pt-4 pb-6 pt-8">
                    <LinkWithQuery to="/" className="flex items-center space-x-2">
                      <ChevronLeftIcon />
                      <span className="font-black text-xs">VOLTAR AO PAINEL</span>
                    </LinkWithQuery>
                  </div>
                  <div className="px-4 pb-2 pt-2">
                    <Text className="text-xl font-medium">Documentação</Text>
                    <Text>Guia para integração</Text>
                  </div>
                  <div>
                    <div>
                      <Text className="px-4 py-4 text-xl font-medium">
                        Integração
                      </Text>
                      <MenuList>
                        <MenuItem>
                          <ListItemIcon>
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Envio de mensagem
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Mensagem simples
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-file">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Mensagem com arquivo
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-split">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Mensagem com split
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-pix">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Mensagem PIX
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-cloud-official">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <div className="flex items-center space-x-2">
                              <Text>
                                Mensagem API oficial
                              </Text>
                              <span className="text-xs font-black text-[#02b7fb]">NOVO</span>
                            </div>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-cloud-official">
                          <ListItemText className="py-1">
                            <div className="pl-6 flex items-center space-x-2">
                              <SubdirectoryArrowRightIcon fontSize="medium" />
                              <Text>
                                Mensagem simples
                              </Text>
                              <span className="text-xs font-black text-[#02b7fb]">NOVO</span>
                            </div>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-cloud-official--buttons">
                          <ListItemText className="py-1">
                            <div className="pl-6 flex items-center space-x-2">
                              <SubdirectoryArrowRightIcon fontSize="medium" />
                              <Text>
                                Mensagem botões
                              </Text>
                              <span className="text-xs font-black text-[#02b7fb]">NOVO</span>
                            </div>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#message-cloud-official--pix">
                          <ListItemText className="py-1">
                            <div className="pl-6 flex items-center space-x-2">
                              <SubdirectoryArrowRightIcon fontSize="medium" />
                              <Text>
                                Mensagem PIX
                              </Text>
                              <span className="text-xs font-black text-[#02b7fb]">NOVO</span>
                            </div>
                          </ListItemText>
                        </MenuItem>
                        {/* <MenuItem to="/">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Playground
                            </Text>
                          </ListItemText>
                        </MenuItem> */}
                      </MenuList>
                    </div>
                    <div className="mt-3">
                      <Text className="px-4 py-4 text-xl font-medium">
                        SmartFilter
                      </Text>
                      <MenuList>
                        <MenuItem component={Link} href="#smart-filter">
                          <ListItemIcon>
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <div className="flex items-center space-x-2">
                              <Text>
                                Visão geral
                              </Text>
                              <span className="text-xs font-black text-emerald-500">NOVO</span>
                            </div>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#smart-filter-modos">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Modos e ativação
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#smart-filter-receitas">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Receitas
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#smart-filter-teto">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Teto de envios
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem component={Link} href="#smart-filter-devedores">
                          <ListItemIcon className="pr-4">
                            <SubdirectoryArrowRightIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              Notificar só devedores
                            </Text>
                          </ListItemText>
                        </MenuItem>
                      </MenuList>
                    </div>
                    {/* <div className="mt-3">
                      <Text className="px-8 py-4 text-xl font-medium">
                        Provedores
                      </Text>
                      <MenuList>
                        <MenuItem to="/">
                          <ListItemIcon className="px-4">
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              MKAUTH
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem to="/">
                          <ListItemIcon className="px-4">
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              IXC
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem to="/">
                          <ListItemIcon className="px-4">
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              WHMCS
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem to="/">
                          <ListItemIcon className="px-4">
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              SGP
                            </Text>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem to="/">
                          <ListItemIcon className="px-4">
                            <FolderIcon fontSize="medium" />
                          </ListItemIcon>
                          <ListItemText className="py-1">
                            <Text>
                              BEESWEB
                            </Text>
                          </ListItemText>
                        </MenuItem>
                      </MenuList>
                    </div> */}
                  </div>
                </div>
              </Paper>
            </div>
          </div>
          <div className="max-w-[1200px] mx-auto px-2 md:px-6 w-full">
            <div className="py-20">
              <div>
                <div className="flex items-end space-x-2">
                  <Logo className="w-[240px]" />
                  <Text className="text-xl font-light">
                    DEVELOPERS
                  </Text>
                </div>
                <Text className="text-xl">
                  Notificações Inteligentes
                </Text>
              </div>
              <div className="grid md:grid-cols-2">
                <div className="py-16">
                  <div className="mt-8">
                    <Text className="text-xl font-light">
                      Com a api <span className="font-medium">Notificacões Inteligentes</span> você pode efetuar envios de mensagens através de qualquer aplicação.
                    </Text>
                  </div>
                </div>
                <div ref={airplanesRef} className="relative hidden md:flex h-[200px] items-center justify-center">
                  <img src={imageHsAirplanes} className="opacity-0 absolute top-[40px] left-0 w-[60%]" />
                  <img src={imageHsAirplanes} className="opacity-0 absolute top-0 left-[150px] w-[80%]" />
                </div>
              </div>
              <div className="mb-12">
                <Text className="text-xl md:text-3xl">
                  Índice
                </Text>
                <div className="mt-4 space-y-2">
                  <Link href="#message-cloud-official" underline="none" className="block">
                    <div className="flex space-x-2 items-center">
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-[#02b7fb]">POST</span>
                        <Text>ou</Text>
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-purple-600">GET</span>
                      </div>
                      <Text component="span">
                        <ChevronRightIcon />
                      </Text>
                      <Text className="text-lg font-black">
                        Envio de mensagem API oficial <span className="p-1 py-0 bg-[#02b7fb] font-black text-white">WhatsappCloudApi</span>
                      </Text>
                      <WhatshotIcon className="text-[#02b7fb]" />
                    </div>
                  </Link>
                  <Link href="#message" underline="none" className="block">
                    <div className="flex space-x-2 items-center">
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-[#02b7fb]">POST</span>
                        <Text>ou</Text>
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-purple-600">GET</span>
                      </div>
                      <Text component="span">
                        <ChevronRightIcon />
                      </Text>
                      <Text className="text-lg">
                        Envio de mensagem simples
                      </Text>
                    </div>
                  </Link>
                  <Link href="#message-file" underline="none" className="block">
                    <div className="flex space-x-2 items-center">
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-[#02b7fb]">POST</span>
                        <Text>ou</Text>
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-purple-600">GET</span>
                      </div>
                      <Text component="span">
                        <ChevronRightIcon />
                      </Text>
                      <Text className="text-lg">
                        Envio de mensagem com arquivo, usando a tag especial <span className="p-1 py-0 bg-[#02b7fb] font-black text-white">SendFile</span>
                      </Text>
                    </div>
                  </Link>
                  <Link href="#message-file" underline="none" className="block">
                    <div className="flex space-x-2 items-center">
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-[#02b7fb]">POST</span>
                        <Text>ou</Text>
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-purple-600">GET</span>
                      </div>
                      <Text component="span">
                        <ChevronRightIcon />
                      </Text>
                      <Text className="text-lg">
                        Envio de mensagem com arquivo e mensagem
                      </Text>
                    </div>
                  </Link>
                  <Link href="#message-split" underline="none" className="block">
                    <div className="flex space-x-2 items-center">
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-[#02b7fb]">POST</span>
                        <Text>ou</Text>
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-purple-600">GET</span>
                      </div>
                      <Text component="span">
                        <ChevronRightIcon />
                      </Text>
                      <Text className="text-lg">
                        Envio de mensagem com split, arquivo e mensagem, usando a tag especial <span className="p-1 py-0 bg-[#02b7fb] font-black text-white">NewLine</span>
                      </Text>
                    </div>
                  </Link>
                  <Link href="#message-pix" underline="none" className="block">
                    <div className="flex space-x-2 items-center">
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-[#02b7fb]">POST</span>
                        <Text>ou</Text>
                        <span className="flex justify-center w-[58px] py-0 font-black text-white bg-purple-600">GET</span>
                      </div>
                      <Text component="span">
                        <ChevronRightIcon />
                      </Text>
                      <Text className="text-lg">
                        Envio de mensagem PIX <span className="p-1 py-0 bg-[#02b7fb] font-black text-white">Pix</span>
                      </Text>
                    </div>
                  </Link>
                </div>
              </div>
              <div className="mb-12">
                <div className="bg-[#02b7fb] bg-opacity-[0.1] rounded p-4 md:p-8 border-l-4 border-[#02b7fb]">
                  <div className="flex items-center space-x-2">
                    <LightbulbIcon className="text-[#02b7fb]" style={{ fontSize: 40 }} />
                    <Text className="font-black text-2xl text-[#02b7fb]">
                      Importante
                    </Text>
                  </div>
                  <div className="mt-2 ml-[48px]">
                    <Text className="font-medium">
                      Há duas formas de fazer envios através da api, via <span className="py-0 px-1 bg-[#02b7fb] text-white font-black">POST</span> e via <span className="py-0 px-1 bg-[#02b7fb] text-white font-black">GET</span> através de parâmetros na url. Escolha o método que melhor se adequa em sua aplicação.
                    </Text>
                  </div>
                </div>
              </div>
              {/* <div className="mb-12">
                <Text className="text-xl md:text-3xl">
                  Playground
                </Text>
                <div className="mt-4 space-y-2">
                  <div className="flex space-x-2 items-center">
                    <Text component="span">
                      <AutoAwesomeIcon className="text-[#02b7fb]" style={{ fontSize: 30 }} />
                    </Text>
                    <Text className="text-lg font-bold text-[#02b7fb]">
                      Testar e criar templates de mensagens
                    </Text>
                  </div>
                </div>
              </div> */}
              <div>
                <div className="mt-4">
                  <div className="flex items-center space-x-3">
                    <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                    <Text className="text-xl md:text-3xl">
                      Endpoint para envio de mensagem
                    </Text>
                  </div>
                  <div className="mt-4">
                    <div className="p-4 bg-gray-400 bg-opacity-[0.3] rounded-sm border-l-4 border-[#02b7fb]">
                      <Text className="text-xs font-light">
                        ENDPOINT
                      </Text>
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                        <ChevronRightIcon className="text-[#02b7fb]" />
                        <Text className="font-medium text-lg">
                          https://myhs.app/sendMessage
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 mb-4 flex items-center space-x-2">
                    <span className="block px-2 py-0 font-black text-white bg-[#02b7fb]">BODY</span>
                      <Text className="font-medium text-xl">
                        Corpo da requisição
                      </Text>
                    </div>
                    <div>
                      <Paper elevation={5}>
                        <div className="border-l-4 border-[#02b7fb]">
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4 bg-gray-400 bg-opacity-[0.1]">
                              <div className="p-4">
                                <Text>Campo</Text>
                              </div>
                            </div>
                            <div className="col-span-8 bg-gray-400 bg-opacity-[0.1]">
                              <div className="p-4">
                                <Text>Valor</Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">u</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>{userApi}</Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">p</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>xxxxxxxxxxx</Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">to</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4 flex space-x-4">
                                <Text>5511999888777</Text>
                                <span className="block px-2 py-0 font-black text-white bg-[#02b7fb]">com ddi e ddd</span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">msg</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>
                                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Paper>
                  </div>
                </div>
                <div className="mt-12">
                  <div className="flex items-center space-x-3">
                    <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                    <Text className="text-xl md:text-3xl">
                      Endpoint para envio de mensagem
                    </Text>
                  </div>
                  <div className="mt-2">
                    <div className="p-4 bg-gray-400 bg-opacity-[0.3] rounded-sm border-l-4 border-purple-600">
                      <Text className="text-xs font-light">
                        ENDPOINT
                      </Text>
                      <div className="flex items-center space-x-2">
                        <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                        <ChevronRightIcon className="text-purple-600" />
                        <Text className="font-medium text-lg">
                          https://myhs.app/sendMessage?app={'<app>'}&u={'<u>'}&p={'<p>'}&to={'<to>'}&msg={'<msg>'}
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 mb-4 flex items-center space-x-2">
                    <span className="block px-2 py-0 font-black text-white bg-purple-600">PARÂMETROS</span>
                      <Text className="font-medium text-xl">
                        Corpo da requisição
                      </Text>
                    </div>
                    <div>
                      <Paper elevation={5}>
                        <div className="border-l-4 border-purple-600">
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4 bg-gray-400 bg-opacity-[0.1]">
                              <div className="p-4">
                                <Text>Parâmetro</Text>
                              </div>
                            </div>
                            <div className="col-span-8 bg-gray-400 bg-opacity-[0.1]">
                              <div className="p-4">
                                <Text>Valor</Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">app</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>
                                  webservices | mkauth | ixc | sgp | bess
                                </Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">u</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>{userApi}</Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">p</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>xxxxxxxxxxx</Text>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">to</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4 flex space-x-4">
                                <Text>5511999888777</Text>
                                <span className="block px-2 py-0 font-black text-white bg-purple-600">com ddi e ddd</span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-12">
                            <div className="col-span-4 border-r-4">
                              <div className="p-4">
                                <Text className="text-lg font-medium">msg</Text>
                              </div>
                            </div>
                            <div className="col-span-8">
                              <div className="p-4">
                                <Text>
                                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Paper>
                  </div>
                </div>
                <div className="mt-8">
                  <div className="mt-16">
                    <div className="mb-8">
                      <Text className="font-medium text-2xl">
                        Exemplos de envios
                      </Text>
                    </div>
                    <div className="space-y-12">
                      <div id="message">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio de mensagem simples
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_SIMPLE_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg: '${MESSAGE_SIMPLE_TEMPLATE}'
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-[70%] p-2 bg-emerald-700 rounded shadow">
                                          <Text className="text-white font-medium">
                                            Olá! Sua fatura vence nos próximos 5 dias.
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem simples',
                                      template: MESSAGE_SIMPLE_TEMPLATE,
                                      httpMethod: 'POST'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>

                        {/* GET */}
                        <div className="mt-12">
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-purple-600" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                            <Text className="font-medium text-xl">
                              Envio de mensagem simples
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>Olá! Sua fatura vence nos próximos 5 dias.</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg: '${MESSAGE_SIMPLE_TEMPLATE}'
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-[70%] p-2 bg-emerald-700 rounded shadow">
                                          <Text className="text-white font-medium">
                                            {MESSAGE_SIMPLE_TEMPLATE}
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem simples',
                                      template: MESSAGE_SIMPLE_TEMPLATE,
                                      httpMethod: 'GET'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div id="message-file">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio somente do arquivo
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Importante</Text>
                                          <Text className="font-medium">
                                            Envie o conteúdo em uma única linha, igual no exemplo a seguir
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_FILE_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg: '${MESSAGE_FILE_TEMPLATE}'
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div className="bg-black bg-opacity-[0.1] p-4">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-[35px] h-[40px] bg-red-500 rounded flex justify-center items-center">
                                                <Text className="text-white font-black text-sm opacity-[0.5]">PDF</Text>
                                              </div>
                                              <div>
                                                <Text className="text-white">fatura.pdf</Text>
                                                <Text className="font-light text-white">PDF 13kb</Text>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem somente com arquivo',
                                      template: MESSAGE_FILE_TEMPLATE,
                                      httpMethod: 'POST'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>

                        {/* GET */}
                        <div className="mt-12">
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-purple-600" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                            <Text className="font-medium text-xl">
                              Envio somente do arquivo
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Importante</Text>
                                          <Text className="font-medium">
                                            Envie o conteúdo em uma única linha, igual no exemplo a seguir
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_FILE_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const parameters = new URLSearchParams({
  app: 'webservices',
  u: '${userApi}',
  p: 'xxxxxxxxxxx',
  to: '5511999888777',
  msg: '${MESSAGE_FILE_TEMPLATE}'
})

fetch(endpoint + '?' + parameters)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div className="bg-black bg-opacity-[0.1] p-4">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-[35px] h-[40px] bg-red-500 rounded flex justify-center items-center">
                                                <Text className="text-white font-black text-sm opacity-[0.5]">PDF</Text>
                                              </div>
                                              <div>
                                                <Text className="text-white">fatura.pdf</Text>
                                                <Text className="font-light text-white">PDF 13kb</Text>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem somente com arquivo',
                                      template: MESSAGE_FILE_TEMPLATE,
                                      httpMethod: 'GET'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div>
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio com mensagem e arquivo
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Importante</Text>
                                          <Text className="font-medium">
                                            Envie o conteúdo em uma única linha, igual no exemplo a seguir
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_FILE_AND_TEXT_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg: '${MESSAGE_FILE_AND_TEXT_TEMPLATE}'
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-[70%] p-2 bg-emerald-700 rounded shadow mb-4">
                                          <Text className="text-white font-medium">
                                            Olá! Sua fatura já está disponível!
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div className="bg-black bg-opacity-[0.1] p-4">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-[35px] h-[40px] bg-red-500 rounded flex justify-center items-center">
                                                <Text className="text-white font-black text-sm opacity-[0.5]">PDF</Text>
                                              </div>
                                              <div>
                                                <Text className="text-white">fatura.pdf</Text>
                                                <Text className="font-light text-white">PDF 13kb</Text>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem e arquivo',
                                      template: MESSAGE_FILE_AND_TEXT_TEMPLATE,
                                      httpMethod: 'POST'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>

                        {/* GET */}
                        <div className="mt-12">
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-purple-600" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                            <Text className="font-medium text-xl">
                              Envio com mensagem e arquivo
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Importante</Text>
                                          <Text className="font-medium">
                                            Envie o conteúdo em uma única linha, igual no exemplo a seguir
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_FILE_AND_TEXT_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const parameters = new URLSearchParams({
  app: 'webservices',
  u: '${userApi}',
  p: 'xxxxxxxxxxx',
  to: '5511999888777',
  msg: '${MESSAGE_FILE_AND_TEXT_TEMPLATE}'
})

fetch(endpoint + '?' + parameters)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-[70%] p-2 bg-emerald-700 rounded shadow mb-4">
                                          <Text className="text-white font-medium">
                                            Olá! Sua fatura já está disponível!
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div className="bg-black bg-opacity-[0.1] p-4">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-[35px] h-[40px] bg-red-500 rounded flex justify-center items-center">
                                                <Text className="text-white font-black text-sm opacity-[0.5]">PDF</Text>
                                              </div>
                                              <div>
                                                <Text className="text-white">fatura.pdf</Text>
                                                <Text className="font-light text-white">PDF 13kb</Text>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem e arquivo',
                                      template: MESSAGE_FILE_AND_TEXT_TEMPLATE,
                                      httpMethod: 'GET'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div id="message-split">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio de mensagem com split
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Importante</Text>
                                          <Text className="font-medium">
                                            Envie o conteúdo em uma única linha, igual no exemplo a seguir
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_FILE_AND_TEXT_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg: '${MESSAGE_FILE_AND_TEXT_TEMPLATE}'
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-[70%] p-2 bg-emerald-700 rounded shadow mb-4">
                                          <Text className="text-white font-medium">
                                            Olá! Sua fatura já está disponível!
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                        <div className="opacity-0 w-[85%] p-2 bg-emerald-700 rounded shadow mb-4">
                                          <Text className="text-white font-medium">
                                            Caso já tenha efetuado o pagamento, favor desconsiderar!
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div className="bg-black bg-opacity-[0.1] p-4">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-[35px] h-[40px] bg-red-500 rounded flex justify-center items-center">
                                                <Text className="text-white font-black text-sm opacity-[0.5]">PDF</Text>
                                              </div>
                                              <div>
                                                <Text className="text-white">fatura.pdf</Text>
                                                <Text className="font-light text-white">PDF 13kb</Text>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem split',
                                      template: MESSAGE_FILE_AND_TEXT_TEMPLATE,
                                      httpMethod: 'POST'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>

                        {/* GET */}
                        <div className="mt-12">
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-purple-600" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                            <Text className="font-medium text-xl">
                              Envio de mensagem com split
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Importante</Text>
                                          <Text className="font-medium">
                                            Envie o conteúdo em uma única linha, igual no exemplo a seguir
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_SPLIT_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const parameters = new URLSearchParams({
  app: 'webservices',
  u: '${userApi}',
  p: 'xxxxxxxxxxx',
  to: '5511999888777',
  msg: '${MESSAGE_SPLIT_TEMPLATE}'
})

fetch(endpoint + '?' + parameters)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-[70%] p-2 bg-emerald-700 rounded shadow mb-4">
                                          <Text className="text-white font-medium">
                                            Olá! Sua fatura já está disponível!
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                        <div className="opacity-0 w-[85%] p-2 bg-emerald-700 rounded shadow mb-4">
                                          <Text className="text-white font-medium">
                                            Caso já tenha efetuado o pagamento, favor desconsiderar!
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div className="bg-black bg-opacity-[0.1] p-4">
                                            <div className="flex items-center space-x-2">
                                              <div className="w-[35px] h-[40px] bg-red-500 rounded flex justify-center items-center">
                                                <Text className="text-white font-black text-sm opacity-[0.5]">PDF</Text>
                                              </div>
                                              <div>
                                                <Text className="text-white">fatura.pdf</Text>
                                                <Text className="font-light text-white">PDF 13kb</Text>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem split',
                                      template: MESSAGE_SPLIT_TEMPLATE,
                                      httpMethod: 'GET'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div id="message-pix">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio de mensagem PIX
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                        <div className="my-4">
                                          <DeprecatedTagsWarning />
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_PIX_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const msg = \`${MESSAGE_PIX_TEMPLATE}\`
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow mb-4">
                                          <img src="https://i.imgur.com/tBv2bfG.png" className="rounded-md overflow-hidden mb-3" />
                                          <Text className="text-white font-bold">
                                            R$100,00
                                          </Text>
                                          <Text className="text-white font-bold">
                                            PLANO MEGA 300GB
                                          </Text>
                                          <Text className="text-white font-medium text-sm">
                                            https://hiperpay.app/pay/HUZQ29HWJRWJ
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem pix',
                                      template: MESSAGE_PIX_TEMPLATE,
                                      httpMethod: 'POST'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>

                        {/* GET */}
                        <div className="mt-12">
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-purple-600" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
                            <Text className="font-medium text-xl">
                              Envio de mensagem PIX
                            </Text>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                        <div className="my-4">
                                          <DeprecatedTagsWarning />
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{MESSAGE_PIX_TEMPLATE}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const msg = \`${MESSAGE_PIX_TEMPLATE}\`
const parameters = new URLSearchParams({
  app: 'webservices',
  u: '${userApi}',
  p: 'xxxxxxxxxxx',
  to: '5511999888777',
  msg
})

fetch(endpoint + '?' + parameters)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow mb-4">
                                          <img src="https://i.imgur.com/tBv2bfG.png" className="rounded-md overflow-hidden mb-3" />
                                          <Text className="text-white font-bold">
                                            R$100,00
                                          </Text>
                                          <Text className="text-white font-bold">
                                            PLANO MEGA 300GB
                                          </Text>
                                          <Text className="text-white font-medium text-sm">
                                            https://hiperpay.app/pay/HUZQ29HWJRWJ
                                          </Text>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4 border-l-4 border-[#02b7fb] bg-black bg-opacity-[0.1]">
                                <button
                                  className="flex items-center justify-start w-full"
                                  onClick={() => {
                                    setOpenTestSendMessageModal(true)
                                    setApiTestData({
                                      title: 'Testar envio de mensagem pix',
                                      template: MESSAGE_PIX_TEMPLATE,
                                      httpMethod: 'GET'
                                    })
                                  }}
                                >
                                  <span className="flex items-center space-x-2">
                                    <ScienceIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                                    <Text className="font-medium text-xl" component="span">
                                      Testar envio
                                    </Text>
                                  </span>
                                  <span className="block ml-auto">
                                    <ChevronRightIcon style={{ fontSize: 35 }} />
                                  </span>
                                </button>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div id="message-cloud-official">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio de <span className="font-black">mensagem simples</span> api oficial
                            </Text>
                            <span className="bg-[#02b7fb] px-1 text-sm font-black text-white">NOVO</span>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 space-y-4">
                                    <div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Exemplo de template cadastrado na Meta com variáveis do tipo número</Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`Fatura disponível para pagamento.
Olá {{1}}, tudo bem?
Sua fatura referente ao contrato Nº {{2}} 
está disponível para pagamento

Dados da fatura:
ID fatura: {{3}}
Vencimento: {{4}}
Valor: {{5}}

Para sua comodidade você pode acessar e emitir sua
fatura pelo link abaixo:
{{6}}

Recomendamos que realize o pagamento até a data de
vencimento para evitar atrasos ou cobranças adicionais`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-12 gap-4 mt-12">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Template</Text>
                                          <Text className="font-medium">
                                            Conteúdo a ser enviado via POST. Informe o nome do seu template cadastrado na Meta.
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{`%WhatsappCloudApi%
  %Template%
    %Name%
      nome_do_template_aqui
    %/Name%
    %VariableNumeric%
      Gustavo
    %/VariableNumeric%
    %VariableNumeric%
      2560
    %/VariableNumeric%
    %VariableNumeric%
      17890
    %/VariableNumeric%
    %VariableNumeric%
      31/12/2025
    %/VariableNumeric%
    %VariableNumeric%
      150,00
    %/VariableNumeric%
    %VariableNumeric%
      https://example.com/pay/4746JOYTC27B.pdf
    %/VariableNumeric%
  %/Template%
%/WhatsappCloudApi%`}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const msg = 'TEMPLATE_DEMONSTRADO_ACIMA'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div>
<p className="text-sm text-white">
Fatura disponível para pagamento.<br />
Olá <span className="font-black">Gustavo</span>, tudo bem?<br />
Sua fatura referente ao contrato Nº <span className="font-black">2560</span> está disponível para pagamento<br />
<br />
Dados da fatura:<br />
ID fatura: <span className="font-black">17890</span><br />
Vencimento: <span className="font-black">31/12/2025</span><br />
Valor: <span className="font-black">150,00</span><br />
<br />
Para sua comodidade você pode acessar e emitir sua fatura pelo link abaixo:<br />
<span className="font-black">https://example.com/pay/4746JOYTC27B.pdf</span><br />
<br />
Recomendamos que realize o pagamento até a data de vencimento para evitar atrasos ou cobranças adicionais<br />
</p>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div id="message-cloud-official--buttons">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio de <span className="font-black">mensagem com botões</span> api oficial
                            </Text>
                            <span className="bg-[#02b7fb] px-1 text-sm font-black text-white">NOVO</span>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 space-y-4">
                                    <div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Exemplo de template cadastrado na Meta com variáveis do tipo número</Text>
                                          <Text className="text-lg">Certifique que seu template na Meta tenha pelo menos 1 botão cadastrado no template</Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`Fatura disponível para pagamento.
Olá {{1}}, tudo bem?
Sua fatura referente ao contrato Nº {{2}} 
está disponível para pagamento

Dados da fatura:
ID fatura: {{3}}
Vencimento: {{4}}
Valor: {{5}}

Para sua comodidade você pode acessar e emitir sua
fatura pelo link abaixo:
{{6}}

Recomendamos que realize o pagamento até a data de
vencimento para evitar atrasos ou cobranças adicionais`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-12 gap-4 mt-12">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Template</Text>
                                          <Text className="font-medium">
                                            Conteúdo a ser enviado via POST. Informe o nome do seu template cadastrado na Meta.
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{`%WhatsappCloudApi%
  %Template%
    %Name%
      nome_do_template_aqui
    %/Name%
    %VariableNumeric%
      Gustavo
    %/VariableNumeric%
    %VariableNumeric%
      2560
    %/VariableNumeric%
    %VariableNumeric%
      17890
    %/VariableNumeric%
    %VariableNumeric%
      31/12/2025
    %/VariableNumeric%
    %VariableNumeric%
      150,00
    %/VariableNumeric%
    %VariableNumeric%
      https://example.com/pay/4746JOYTC27B.pdf
    %/VariableNumeric%
    %ButtonUrl%
      https://example.com/pay/4746JOYTC27B.pdf
    %/ButtonUrl%
    %ButtonUrl%
      https://example.com/account/support
    %/ButtonUrl%
  %/Template%
%/WhatsappCloudApi%`}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const msg = 'TEMPLATE_DEMONSTRADO_ACIMA'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div>
<p className="text-sm text-white">
Fatura disponível para pagamento.<br />
Olá <span className="font-black">Gustavo</span>, tudo bem?<br />
Sua fatura referente ao contrato Nº <span className="font-black">2560</span> está disponível para pagamento<br />
<br />
Dados da fatura:<br />
ID fatura: <span className="font-black">17890</span><br />
Vencimento: <span className="font-black">31/12/2025</span><br />
Valor: <span className="font-black">150,00</span><br />
<br />
Para sua comodidade você pode acessar e emitir sua fatura pelo link abaixo:<br />
<span className="font-black">https://example.com/pay/4746JOYTC27B.pdf</span><br />
<br />
Recomendamos que realize o pagamento até a data de vencimento para evitar atrasos ou cobranças adicionais<br />
</p>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                          <div className="border-t border-[#ffffff2d] divide-y divide-[#ffffff2d] mt-3">
                                            <a href="https://example.com/pay/4746JOYTC27B.pdf" target="_blank" className="flex justify-center items-center space-x-2 w-full py-2">
                                              <OpenInNewIcon style={{ fontSize: 17 }} className="text-white" />
                                              <span className="block text-center text-xs font-black text-white">
                                                Pagar agora
                                              </span>
                                            </a>
                                            <a href="https://example.com/account/support" target="_blank" className="flex justify-center items-center space-x-2 w-full py-2">
                                              <OpenInNewIcon style={{ fontSize: 17 }} className="text-white" />
                                              <span className="block text-center text-xs font-black text-white">
                                                Suporte
                                              </span>
                                            </a>
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      {/*  */}

                      <div id="message-cloud-official--pix">
                        <div>
                          <div className="mb-2 flex items-center space-x-2">
                            <BookmarkIcon style={{ fontSize: 35 }} className="text-[#02b7fb]" />
                            <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
                            <Text className="font-medium text-xl">
                              Envio de <span className="font-black">mensagem com pix</span> api oficial
                            </Text>
                            <span className="bg-[#02b7fb] px-1 text-sm font-black text-white">NOVO</span>
                          </div>
                          <Paper elevation={5}>
                            <div>
                              <div className="p-4">
                                <div className="flex items-center space-x-2 mb-4">
                                  <Text className="font-black text-lg">
                                    Exemplo de requisição
                                  </Text>
                                </div>
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 space-y-4">
                                    <div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Exemplo de template cadastrado na Meta com variáveis do tipo número</Text>
                                          <Text className="text-lg">Certifique que seu template na Meta tenha pelo menos 1 botão cadastrado no template, o link para pagamento com pix será injetado no botão através da variável <span className="font-black">{`{{PAYMENT_LINK_CODE}}`}</span></Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`Fatura disponível para pagamento.
Olá {{1}}, tudo bem?
Sua fatura referente ao contrato Nº {{2}} 
está disponível para pagamento

Dados da fatura:
ID fatura: {{3}}
Vencimento: {{4}}
Valor: {{5}}

Para sua comodidade você pode acessar e emitir sua
fatura pelo link abaixo:
{{6}}

Recomendamos que realize o pagamento até a data de
vencimento para evitar atrasos ou cobranças adicionais`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-12 gap-4 mt-12">
                                  <div className="col-span-12 md:col-span-8 space-y-4">
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Conteúdo a ser enviado
                                        </Text>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">Template</Text>
                                          <Text className="font-medium">
                                            Conteúdo a ser enviado via POST. Informe o nome do seu template cadastrado na Meta.
                                          </Text>
                                        </div>
                                      </div>
                                      <div class="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
                                        <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
                                        <div>
                                          <Text className="font-black text-lg">PaymentLink tags</Text>
                                          <Text className="font-medium">
                                            Para ver mais tags <span className="font-black">%PaymentLink%</span> disponível, acesse <span className="font-black"><LinkWithQuery to="/documentation#message-pix">Mensagem PIX</LinkWithQuery></span>
                                          </Text>
                                        </div>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code>{`%PaymentLink%
  %Pix%
    %PixCode%
      00020101021226940014BR.GOV.BCB.PIX2572qrco...
    %/PixCode%
  %/Pix%
%/PaymentLink%
%WhatsappCloudApi%
  %Template%
    %Name%
      nome_do_template_aqui
    %/Name%
    %VariableNumeric%
      Gustavo
    %/VariableNumeric%
    %VariableNumeric%
      2560
    %/VariableNumeric%
    %VariableNumeric%
      17890
    %/VariableNumeric%
    %VariableNumeric%
      31/12/2025
    %/VariableNumeric%
    %VariableNumeric%
      150,00
    %/VariableNumeric%
    %VariableNumeric%
      https://example.com/pay/4746JOYTC27B.pdf
    %/VariableNumeric%
    %ButtonUrl%
      {{PAYMENT_LINK_CODE}}
    %/ButtonUrl%
  %/Template%
%/WhatsappCloudApi%`}</Code>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="mb-2">
                                        <Text>
                                          Exemplo em <span className="font-medium">Javascript</span>
                                        </Text>
                                      </div>
                                      <div className="rounded-md overflow-hidden">
                                        <Code language="javascript" wrapLongLines={false}>
{`const endpoint = 'https://myhs.app/sendMessage'
const msg = 'TEMPLATE_DEMONSTRADO_ACIMA'
const options = {
  method: 'POST',
  headers: {
    accept: 'application/json',
    'content-type': 'application/json'
  },
  body: JSON.stringify({
    u: '${userApi}',
    p: 'xxxxxxxxxxx',
    to: '5511999888777',
    msg
  })
}

fetch(endpoint, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err))`}
                                        </Code>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="relative col-span-12 md:col-span-4">
                                    <div className="sticky top-[20px]">
                                      <div className="mb-2">
                                        <Text>
                                          Resultado
                                        </Text>
                                      </div>
                                      <PhoneDevice>
                                        <div className="opacity-0 w-full p-2 bg-emerald-700 rounded shadow">
                                          <div>
<p className="text-sm text-white">
Fatura disponível para pagamento.<br />
Olá <span className="font-black">Gustavo</span>, tudo bem?<br />
Sua fatura referente ao contrato Nº <span className="font-black">2560</span> está disponível para pagamento<br />
<br />
Dados da fatura:<br />
ID fatura: <span className="font-black">17890</span><br />
Vencimento: <span className="font-black">31/12/2025</span><br />
Valor: <span className="font-black">150,00</span><br />
<br />
Para sua comodidade você pode acessar e emitir sua fatura pelo link abaixo:<br />
<span className="font-black">https://example.com/pay/4746JOYTC27B.pdf</span><br />
<br />
Recomendamos que realize o pagamento até a data de vencimento para evitar atrasos ou cobranças adicionais<br />
</p>
                                          </div>
                                          <div className="flex justify-end">
                                            <DoneAllIcon className="text-white" style={{ fontSize: 16 }} />
                                          </div>
                                          <div className="border-t border-[#ffffff2d] divide-y divide-[#ffffff2d] mt-3">
                                            <a href="https://example.com/pay/4746JOYTC27B" target="_blank" className="flex justify-center items-center space-x-2 w-full py-2">
                                              <OpenInNewIcon style={{ fontSize: 17 }} className="text-white" />
                                              <span className="block text-center text-xs font-black text-white">
                                                Pagar agora
                                              </span>
                                            </a>
                                          </div>
                                        </div>
                                      </PhoneDevice>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Paper>
                        </div>
                        <div className="mt-8">
                          <EndpointResponseExample />
                        </div>
                      </div>
                      <SmartFilterDocSection />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutMain>
    </LayoutBase>
  )
}

function SmartFilterDocSection() {
  return (
    <div className="mt-16 space-y-16">

      {/* ---- Visão geral ---- */}
      <div id="smart-filter">
        <div className="mb-3 flex items-center space-x-2">
          <ScienceIcon style={{ fontSize: 35 }} className="text-emerald-500" />
          <Text className="font-medium text-2xl">
            SmartFilter
          </Text>
          <span className="bg-emerald-600 px-1 text-sm font-black text-white">NOVO</span>
        </div>
        <Paper elevation={5}>
          <div className="p-6 space-y-4">
            <Text className="text-lg">
              O <span className="font-black">SmartFilter</span> é uma camada inteligente entre o seu ERP e o envio no WhatsApp. Cada mensagem é analisada e o filtro decide se <span className="font-black">envia, bloqueia, atrasa</span> ou <span className="font-black">segura para aprovação</span> — antes de gastar uma mensagem paga da Meta.
            </Text>
            <Text className="text-lg">
              Para provedores de internet isso significa <span className="font-black">cortar custo</span>, parar de incomodar o assinante com mensagens repetidas e <span className="font-black">proteger o número</span> de bloqueios por reclamação.
            </Text>
            <div className="p-4 bg-emerald-600 bg-opacity-[0.1] rounded flex space-x-4">
              <DoneAllIcon style={{ fontSize: 30 }} className="text-emerald-500" />
              <div>
                <Text className="font-black text-lg">À prova de falhas</Text>
                <Text>Se o filtro tiver qualquer erro ou demora, a mensagem é <span className="font-black">enviada normalmente</span>. Ele nunca trava o envio, e o seu ERP continua recebendo a mesma resposta de sempre.</Text>
              </div>
            </div>
            <Text>
              Acesse pelo menu <span className="font-black"><LinkWithQuery to="/smart-filter">SmartFilter</LinkWithQuery></span>. Disponível para planos com a função habilitada.
            </Text>
          </div>
        </Paper>
      </div>

      {/* ---- Modos e ativação ---- */}
      <div id="smart-filter-modos">
        <div className="mb-3 flex items-center space-x-2">
          <PlumbingIcon style={{ fontSize: 30 }} className="text-[#02b7fb]" />
          <Text className="font-medium text-xl">
            Modos e ativação
          </Text>
        </div>
        <Paper elevation={5}>
          <div className="p-6 space-y-6">
            <Text>
              O SmartFilter começa <span className="font-black">desligado</span>. Você ativa por etapas, no seu ritmo, em <span className="font-black"><LinkWithQuery to="/smart-filter">Configurações</LinkWithQuery></span>:
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black">Desligado</Text>
                <Text>Não faz nada. Tudo é enviado como antes.</Text>
              </div>
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black">Observação</Text>
                <Text>Analisa e registra tudo, mas <span className="font-black">não bloqueia</span> nada. Serve para enxergar o tráfego.</Text>
              </div>
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black">Simulação</Text>
                <Text>Mostra <span className="font-black">"o que teria bloqueado"</span> sem bloquear de verdade. Ideal para testar regras com segurança.</Text>
              </div>
              <div className="p-4 rounded bg-emerald-600 bg-opacity-[0.1]">
                <Text className="font-black text-emerald-600">Ativo</Text>
                <Text>Aplica as regras de verdade: bloqueia, atrasa e segura para aprovação.</Text>
              </div>
            </div>
            <div className="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
              <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
              <div>
                <Text className="font-black text-lg">Lista-negra x Lista-branca</Text>
                <Text><span className="font-black">Lista-negra</span> (padrão): envia tudo, exceto o que suas regras bloquearem. É o modo seguro para começar.</Text>
                <Text className="mt-2"><span className="font-black">Lista-branca</span>: bloqueia tudo, exceto o que suas regras liberarem. Use quando quer enviar só um tipo de mensagem (ex.: só faturas com vencimento real).</Text>
              </div>
            </div>
          </div>
        </Paper>
      </div>

      {/* ---- Receitas ---- */}
      <div id="smart-filter-receitas">
        <div className="mb-3 flex items-center space-x-2">
          <BookmarkIcon style={{ fontSize: 30 }} className="text-[#02b7fb]" />
          <Text className="font-medium text-xl">
            Receitas (regras de 1 clique)
          </Text>
        </div>
        <Paper elevation={5}>
          <div className="p-6 space-y-6">
            <Text>
              Receitas são regras prontas que você instala com 1 clique e ajusta (dias, valores, horários) sem mexer em código. O filtro lê cada mensagem e extrai <span className="font-black">fatos</span>: a intenção (cobrança, atraso, desbloqueio, marketing…), os <span className="font-black">dias até o vencimento</span>, os valores, se tem PIX/boleto e até se sobrou uma <span className="font-black">variável não preenchida</span> (ex.: <span className="font-black">#areceber_data_vencimento#</span>).
            </Text>

            <div className="space-y-4">
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black text-lg">🛡️ Anti-desperdício</Text>
                <ul className="space-y-1 ml-5 mt-2 list-disc">
                  <li><Text>Não repetir a mesma mensagem (dedup numa janela de minutos)</Text></li>
                  <li><Text>No máximo X mensagens por cliente/dia</Text></li>
                  <li><Text>Respeitar um intervalo mínimo entre mensagens</Text></li>
                  <li><Text>Lista de não-perturbe (números que nunca recebem)</Text></li>
                </ul>
              </div>
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black text-lg">📅 Cobrança inteligente</Text>
                <ul className="space-y-1 ml-5 mt-2 list-disc">
                  <li><Text>Só avisar quando a fatura tiver vencimento de verdade</Text></li>
                  <li><Text>Não cobrar cedo demais (antes de X dias do vencimento)</Text></li>
                  <li><Text>Janela D-2 até D+5, ou só faturas já vencidas</Text></li>
                  <li><Text>Não cobrar valores pequenos</Text></li>
                  <li><Text>Cobrança alta pede conferência (vai para aprovação)</Text></li>
                </ul>
              </div>
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black text-lg">🧩 Qualidade da mensagem</Text>
                <ul className="space-y-1 ml-5 mt-2 list-disc">
                  <li><Text>Bloquear variável não preenchida (#var#, %var%, {'{var}'})</Text></li>
                  <li><Text>Mensagem não reconhecida pede conferência</Text></li>
                </ul>
              </div>
              <div className="p-4 rounded bg-gray-500 bg-opacity-[0.08]">
                <Text className="font-black text-lg">🌙 Horário e timing</Text>
                <ul className="space-y-1 ml-5 mt-2 list-disc">
                  <li><Text>Nada de madrugada (segura e envia no horário comercial)</Text></li>
                  <li><Text>Promoção só em horário comercial</Text></li>
                  <li><Text>Não incomodar no domingo</Text></li>
                </ul>
              </div>
              <div className="p-4 rounded bg-emerald-600 bg-opacity-[0.1]">
                <Text className="font-black text-lg text-emerald-600">🔓 Garantias (sempre passa)</Text>
                <ul className="space-y-1 ml-5 mt-2 list-disc">
                  <li><Text>Desbloqueio/reativação sempre passa (crítico para o cliente)</Text></li>
                  <li><Text>Lista de sempre enviar (números que ignoram todos os filtros)</Text></li>
                </ul>
              </div>
            </div>

            <div className="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
              <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
              <div>
                <Text className="font-black text-lg">Fila de aprovação e Lista de bloqueio</Text>
                <Text>Mensagens marcadas para conferência caem na aba <span className="font-black">Aprovações</span>, onde você aprova ou descarta. E a <span className="font-black">Lista de bloqueio</span> aceita importação em massa — cole os números exportados do ERP (cancelados, comodato, quem pediu para parar) de uma vez.</Text>
              </div>
            </div>
          </div>
        </Paper>
      </div>

      {/* ---- Teto de envios ---- */}
      <div id="smart-filter-teto">
        <div className="mb-3 flex items-center space-x-2">
          <WhatshotIcon style={{ fontSize: 30 }} className="text-[#02b7fb]" />
          <Text className="font-medium text-xl">
            Teto de envios (trava de gasto)
          </Text>
        </div>
        <Paper elevation={5}>
          <div className="p-6 space-y-4">
            <Text>
              O teto limita o <span className="font-black">total de mensagens</span> enviadas numa janela — um <span className="font-black">mês que reseta sozinho</span> ou um período de <span className="font-black">datas fixas</span>. Ao atingir o limite, o filtro descarta (ou segura) o excedente. É a sua <span className="font-black">garantia de gasto máximo</span>.
            </Text>
            <div className="p-4 bg-emerald-600 bg-opacity-[0.1] rounded flex space-x-4">
              <LightbulbIcon style={{ fontSize: 30 }} className="text-emerald-500" />
              <div>
                <Text className="font-black text-lg">Exemplo</Text>
                <Text>"No máximo <span className="font-black">30.000 envios</span> em maio." Ao bater 30.000, o filtro para de enviar até o mês virar. O painel mostra o consumo (ex.: 22.000/30.000) e projeta quando o teto deve estourar.</Text>
              </div>
            </div>
          </div>
        </Paper>
      </div>

      {/* ---- Notificar só devedores ---- */}
      <div id="smart-filter-devedores">
        <div className="mb-3 flex items-center space-x-2">
          <LightbulbIcon style={{ fontSize: 30 }} className="text-emerald-500" />
          <Text className="font-medium text-xl">
            Receita: notificar só quem deve de verdade
          </Text>
        </div>
        <Paper elevation={5}>
          <div className="p-6 space-y-4">
            <Text>
              Um caso comum: enviar cobrança <span className="font-black">só para quem está realmente devendo</span>, sem gastar mensagem com quem já pagou ou nem venceu. Monte assim:
            </Text>
            <ol className="space-y-2 ml-6 list-decimal">
              <li><Text>Ligue o modo <span className="font-black">Lista-branca</span> ("enviar só o que minhas regras permitirem").</Text></li>
              <li><Text>Instale <span className="font-black">"Só faturas já vencidas"</span> — libera apenas cobrança de conta que passou do vencimento.</Text></li>
              <li><Text>Instale <span className="font-black">"Bloquear variável não preenchida"</span> — evita mandar <span className="font-black">#areceber_data_vencimento#</span> literal.</Text></li>
            </ol>
            <div className="p-4 bg-purple-600 bg-opacity-[0.1] rounded flex space-x-4">
              <InfoIcon style={{ fontSize: 30 }} className="text-purple-600" />
              <div>
                <Text className="font-black text-lg">E contratos em comodato / bloqueados?</Text>
                <Text>Saber se um cliente é "bom pagador" ou se o contrato é comodato é uma informação que mora no ERP, não na mensagem. Hoje você resolve exportando esses números do ERP e colando na <span className="font-black">Lista de bloqueio</span> em massa. Em breve, o ERP poderá enviar o status junto da mensagem para o filtro decidir sozinho.</Text>
              </div>
            </div>
          </div>
        </Paper>
      </div>

    </div>
  )
}

function EndpointResponseExample() {
  return (
    <div>
      <div className="mb-4 flex items-center space-x-2">
        <Text className="font-medium text-xl">
          Respostas
        </Text>
        <span className="flex justify-center w-[58px] font-black text-white bg-[#02b7fb]">POST</span>
        <Text>e</Text>
        <span className="flex justify-center w-[58px] font-black text-white bg-purple-600">GET</span>
      </div>
      <div className="space-y-2">
        <div className="bg-gray-300 bg-opacity-[0.4] p-4 rounded">
          <div className="flex items-center space-x-2">
            <button>
              <Text component="span">
                <ExpandMoreIcon style={{ fontSize: 35 }} />
              </Text>
            </button>
            <Text className="font-black">
              201 Success
            </Text>
          </div>
          <div>
            <div className="rounded-md overflow-hidden">
              <Code>
{`{
  result: "success",
  status: 201,
  date: Date,
  message: "mensagem adicionada a fila"
}`}
              </Code>
            </div>
          </div>
        </div>
        <div className="bg-gray-300 bg-opacity-[0.4] p-4 rounded">
          <div className="flex items-center space-x-2">
            <button>
              <Text component="span">
                <ExpandMoreIcon style={{ fontSize: 35 }} />
              </Text>
            </button>
            <Text className="font-black">
              400 Bad request
            </Text>
          </div>
          <div>
            <div className="rounded-md overflow-hidden">
              <Code>
{`{
  result: "error",
  status: 400,
  date: Date,
  message: "<error>"
}`}
              </Code>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function DeprecatedTagsWarning() {
  return (
    <div class="p-4 bg-orange-600 bg-opacity-[0.1] rounded flex space-x-4">
      <InfoIcon style={{ fontSize: 30 }} className="text-orange-600" />
      <div>
        <Text className="font-black text-lg">TAGS DEPRECATED</Text>
        <Text className="font-black">Tags que serão removidas no futuro, mas que continuarão funcionando até sua remoção completa</Text>
        <Text>PixTitle, PixSubtitle, PixPrice, PixLogoUrl, PixInvoiceUrl, PixInfo, PixPrimaryColor, PixThumbnail.</Text>
        <Text className="font-black mt-3">Recomendamos que passe a utilizar as tags abaixo</Text>
      </div>
    </div>
  )
}
